// src/hooks/usePaymentStatus.ts
import { useSubscription } from './useSubscription';

interface PaymentStatusReturn {
  isPastDue: boolean;
  isSuspended: boolean;
  daysRemainingInGracePeriod: number | null;
  shouldShowBanner: boolean;
  shouldBlockAccess: boolean;
}

/**
 * Custom hook to check user's payment/subscription status
 * Returns status flags for payment failure warnings
 */
export function usePaymentStatus(): PaymentStatusReturn {
  const { subscription, isLoading } = useSubscription();

  // Check if subscription is past due
  const isPastDue = subscription?.status === 'past_due';

  // Check if subscription is suspended (unpaid or incomplete)
  const isSuspended = subscription?.status === 'unpaid' ||
                      subscription?.status === 'incomplete';

  // Calculate days remaining in the 7-day grace period set by the backend on payment failure.
  const daysRemainingInGracePeriod = subscription?.gracePeriodEnd
    ? Math.max(
        0,
        Math.ceil(
          (new Date(subscription.gracePeriodEnd).getTime() - Date.now()) /
          (1000 * 60 * 60 * 24)
        )
      )
    : null;

  // Show banner if past due and not dismissed (or no days remaining)
  const shouldShowBanner = isPastDue && !isLoading;

  // Block access if suspended
  const shouldBlockAccess = isSuspended && !isLoading;

  return {
    isPastDue,
    isSuspended,
    daysRemainingInGracePeriod,
    shouldShowBanner,
    shouldBlockAccess
  };
}

